import { useState } from "react";
import { useSEO } from "../hooks/useSEO";
import { contentAPI } from "../lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useSEO({
    title: "Contact TaxShift 2026",
    description: "Get in touch with the TaxShift editorial desk. Story tips, corrections, and partnership inquiries welcome.",
    path: "/contact",
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await contentAPI.contact(form);
      setSubmitted(true);
      toast.success("Message received. We'll be in touch.");
    } catch (e) {
      toast.error("Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-16 md:py-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <span className="font-mono uppercase text-[10px] tracking-[0.3em] text-muted-foreground mb-4 block">Contact</span>
        <h1 className="font-heading text-5xl md:text-6xl font-bold tracking-tight mb-6" data-testid="contact-title">
          Write to the <span className="italic">editorial desk</span>.
        </h1>
        <p className="font-body text-lg text-foreground/80 mb-10">
          Story tips, corrections, partnership inquiries, or feedback — we read every message.
        </p>

        {submitted ? (
          <div className="border-2 border-primary bg-primary/5 p-8" data-testid="contact-success">
            <h2 className="font-heading text-3xl font-semibold mb-3">Thank you.</h2>
            <p className="font-body text-base">
              Your message has been received. We respond to editorial inquiries within three
              business days.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5 bg-card border-2 border-foreground brutalist-shadow p-6 md:p-8" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="font-body text-sm font-semibold block mb-2">Name</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 font-mono border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="font-body text-sm font-semibold block mb-2">Email</label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 font-mono border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
                  data-testid="contact-email"
                />
              </div>
            </div>
            <div>
              <label className="font-body text-sm font-semibold block mb-2">Subject</label>
              <input
                required
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="w-full px-4 py-3 font-mono border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
                data-testid="contact-subject"
              />
            </div>
            <div>
              <label className="font-body text-sm font-semibold block mb-2">Message</label>
              <textarea
                required
                rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full px-4 py-3 font-mono border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background resize-y"
                data-testid="contact-message"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="w-full bg-primary text-primary-foreground font-mono uppercase tracking-widest text-sm px-6 py-4 hover:brightness-110 transition flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Send Message"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactPage;
